import React, { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useLocation, Navigate } from "react-router-dom";

import {API_AUTH_USER, HDR_AUTH_MORE, VIEW_LOGIN} from "../../common/EsConstants";
import { esMoreSet } from "../../common/EsMore";

import { esFetch } from "../../common/EsFetch";
import { EsFlagIdentity, esPinFlags } from "../../common/EsFlags";

const AuthContext = React.createContext( null );

export function useAuth() {
    return React.useContext( AuthContext );
}

export function AuthProvider( { children } ) {
    const [authenticated, setAuthenticated] = useState( false );
    const [user, setUser] = useState( undefined );
    const [account, setAccount] = useState( undefined );
    const [admin, setAdmin] = useState( false );
    // The session's idle limit in seconds, esback's; null when signed out or not told.
    const [sessionSeconds, setSessionSeconds] = useState( null );
    // true, not false. This is "we have not asked esback yet", and it is the
    // state the very first render is in -- the fetch below cannot have answered
    // by then. Starting at false said "asked, and nobody is logged in", which
    // RequireAuth believed.
    const [loading, setLoading] = useState( true ) ;
    // Called for the subscription, not for the value: react-cookie re-renders
    // this provider when XSRF-TOKEN changes, which is what keeps a login in
    // another tab from leaving this one holding a stale auth state. Nothing
    // here reads the cookie, so the binding is dropped rather than the call.
    useCookies( [ 'XSRF-TOKEN' ] );

    useEffect(() => {
        setLoading( true );
        // This used to pass mode: 'no-cors', which did not get round esback
        // having no CORS -- it only made the refusal quiet. An opaque response
        // has an empty body whatever the server said, and an empty body here
        // means "logged out", so a local login could never be seen. There is no
        // second origin to allow any more -- a laptop is one origin through the
        // dev server's proxy -- and a failure is allowed to be a failure.
        esFetch( API_AUTH_USER )
            .then( response => {
                // Before the body: the answer rides a header, signed out included (EsMore).
                esMoreSet( response.headers?.get?.( HDR_AUTH_MORE ) === '1' );
                return response.text();
            } )
            .then( body => {
                if ( body === '' ) {
                    esPinFlags( null );
                    setUser( null );
                    setAccount( null );
                    setAuthenticated( false );
                    setAdmin( false );
                } else {
                    const obj = JSON.parse( body );
                    // Before the reader is published, so the first render that knows
                    // who it is already reads its run's pins. Absent but for a fixture.
                    esPinFlags( obj.flags );
                    setUser( obj.name );
                    // The id, the nickname and the ROLE, all three esback's --
                    // see EsConstants.NM_AUTH_ACCOUNT_ID and NM_AUTH_ROLE. The
                    // id keys the LaunchDarkly context, the role is what a rule
                    // targets on, and the nickname is the only one a screen may
                    // render: `user` is an email address now, and putting it on
                    // a page shows it to the table.
                    //
                    // ⚠️ THE ROLE WAS BEING DROPPED HERE UNTIL 2026-09-16, and
                    // it is the reason client-side tier targeting did not work.
                    // esback has always put it in this body; this line rebuilt
                    // the account from two of the three fields, so
                    // EsFlags.contextFor read `pAccount.role ?? 'PUBLIC'` and
                    // every logged-in reader reached LaunchDarkly as PUBLIC.
                    //
                    // It is invisible from both ends. The server evaluates the
                    // same flag correctly, so a rule looks like it works until
                    // somebody compares what the API allows with what the SPA
                    // draws -- an ALPHA reader would have been served the
                    // feature by esback and denied it by the nav bar.
                    setAccount( obj.accountId == null
                        ? null
                        : { accountId: obj.accountId,
                            nickname: obj.nickname,
                            role: obj.role,
                            // Maker privileges: esback's one rule (EsSubService.isMaker),
                            // so a screen hides what the server would refuse.
                            maker: obj.maker,
                            // A place in a limited group, or the group
                            // they are queueing for -- the welcome page's
                            // "BETA (3 of 100)". Named one by one for the
                            // reason above: a field left off this list is a
                            // field the screen never sees.
                            seat: obj.seat ?? null,
                            seatLimit: obj.seatLimit ?? null,
                            waitlist: obj.waitlist ?? null,
                            locale: obj.locale ?? null,
                            // The locale groups this account translates in: the nav's
                            // Translate entry and /translate's groups. Empty for most.
                            translator: obj.translator ?? [],
                            // "MM-DD" and never the year, for the welcome page's
                            // birthday banner. Absent before a birthdate is given.
                            birthday: obj.birthday ?? null,
                            // When a Guardian ban ends (EsGuardianBanNotice). Absent but for a banned account.
                            bannedUntil: obj.bannedUntil ?? null } );
                    setAuthenticated( true );
                    // Cognito reports group membership here; the pool carries
                    // ROLE_MEMBER and ROLE_ADMIN.
                    const aGroups = obj[ 'cognito:groups' ] || [];
                    setAdmin( aGroups.includes( 'ROLE_ADMIN' ) );
                    setSessionSeconds( obj.sessionSeconds > 0 ? obj.sessionSeconds : null );
                }
                setLoading( false );
            })
            // Without this a rejected fetch left the provider stuck on its
            // initial state with nothing logged, which is the same silence
            // 'no-cors' used to produce.
            .catch( ( error ) => {
                console.error( 'Could not read the logged-in user', error );
                esMoreSet( false );
                esPinFlags( null );
                setUser( null );
                setAccount( null );
                setAuthenticated( false );
                setAdmin( false );
                setLoading( false );
            });
    }, [ setAuthenticated, setUser, setLoading ] )

    // The session ran out under this tab: everything above goes back to signed out, without
    // asking esback again, which has just said so. EsSessionExpiry calls it as it leaves for
    // the login.
    const expire = useCallback( () => {
        esPinFlags( null );
        setUser( null );
        setAccount( null );
        setAuthenticated( false );
        setAdmin( false );
        setSessionSeconds( null );
    }, [] );

   const aExports = { authenticated, user, account, admin, loading, sessionSeconds, expire }

    // EsFlagIdentity rides along here rather than sitting in index.js, because
    // this is where `user` lives. It starts LaunchDarkly once the fetch above
    // has answered, and re-identifies whenever the logged-in user changes, so a
    // flag targeted at a segment evaluates for the real reader. A fixture
    // account is never started or identified at all; see EsFlags.js.
    //
    // It must be BELOW EsFlagProvider in the tree, which index.js arranges, and
    // it renders nothing.
    return <AuthContext.Provider value={ aExports }>
        <EsFlagIdentity user={ user } account={ account } />
        {children}
    </AuthContext.Provider>
}

export function RequireAuth( { children } ) {
    const auth = useAuth();
    const location = useLocation();

    // Nothing at all until the answer is in. "No user yet" and "no user" are
    // different states and this used to treat them as one: on a full page load
    // -- a hard refresh, or a pasted URL -- the first render always had no user,
    // so every protected page redirected to the login page before esback had
    // been asked anything. The redirect had already happened by the time the
    // answer arrived, which is why the login page appears wearing a logged-in
    // nav bar. Clicking through the app never showed it, because the provider
    // mounts once and by then it knows.
    if ( auth.loading ) return null;

    if ( !auth.user ) {
        return <Navigate to={ VIEW_LOGIN } state={{ from: location }} replace />
    }

    return children;
}
